import React, { useState, useRef, useEffect } from 'react';
import { 
  FaHandsHelping, 
  FaBullseye, 
  FaBalanceScale, 
  FaCheckCircle, 
  FaShieldAlt,
  FaLightbulb,
  FaUsers,
  FaRocket,
  FaChartLine
} from 'react-icons/fa';

interface AnimatedCounterProps {
  end: number;
  duration?: number;
  suffix?: string;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef<HTMLSpanElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const startTime = Date.now();
          const startValue = 0;
          
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentCount = Math.floor(startValue + (end - startValue) * easeOutQuart);
            
            setCount(currentCount);
            
            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };
          
          animate();
        }
      },
      { threshold: 0.5 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return <span ref={countRef}>{count}{suffix}</span>;
};

const ValueItem: React.FC<{ icon: React.ReactNode; title: string; description: string; delay: number }> = ({ 
  icon, 
  title, 
  description, 
  delay 
}) => {
  return (
    <div 
      className="flex items-start space-x-4 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-green-accent/30 transition-all duration-500 hover:transform hover:scale-105"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-accent to-blue-accent rounded-lg flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h4 className="font-semibold text-white mb-1">{title}</h4>
        <p className="text-gray-300 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
};

const About: React.FC = () => {
  const [activeCard, setActiveCard] = useState<number | null>(null);

  const stats = [
    { number: 500, suffix: "+", label: "Empresas Asesoradas" },
    { number: 15, suffix: "+", label: "Años de Experiencia" },
    { number: 98, suffix: "%", label: "Satisfacción Cliente" },
    { number: 24, suffix: "/7", label: "Soporte Disponible" }
  ];

  const values = [
    {
      icon: <FaShieldAlt className="w-6 h-6 text-white" />,
      title: "Honestidad Total",
      description: "Transparencia absoluta en cada proceso, sin sorpresas ni costos ocultos."
    },
    {
      icon: <FaRocket className="w-6 h-6 text-white" />,
      title: "Compromiso Garantizado",
      description: "Resultados de calidad entregados en los plazos acordados, siempre."
    },
    {
      icon: <FaBalanceScale className="w-6 h-6 text-white" />,
      title: "Asesoramiento Independiente",
      description: "Recomendaciones objetivas basadas únicamente en el beneficio de tu empresa."
    },
    {
      icon: <FaUsers className="w-6 h-6 text-white" />,
      title: "Confidencialidad Absoluta",
      description: "Tu información empresarial protegida con los más altos estándares de seguridad."
    },
    {
      icon: <FaLightbulb className="w-6 h-6 text-white" />,
      title: "Innovación Constante",
      description: "Soluciones creativas y tecnológicas para los desafíos más complejos."
    }
  ];

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
      {/* Elementos decorativos de fondo */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-accent/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-accent/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-blue-accent/5 via-transparent to-transparent rounded-full"></div>
      </div>

      <div className="relative z-10 container mx-auto px-5 lg:px-32 py-20">
        {/* Hero Section */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-accent to-blue-accent rounded-full mb-6">
            <FaChartLine className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-white via-blue-light to-green-accent bg-clip-text text-transparent mb-6">
            Tu Socio Estratégico
            <span className="block text-2xl md:text-3xl mt-2 text-gray-300 font-normal">
              para el Crecimiento Empresarial
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 leading-relaxed max-w-4xl mx-auto mb-8">
            Más allá de servicios contables tradicionales, te ofrecemos 
            <span className="text-green-accent font-semibold"> claridad total</span>, 
            <span className="text-blue-accent font-semibold"> control absoluto</span> y la 
            <span className="text-green-accent font-semibold"> confianza necesaria</span> para 
            tomar decisiones que impulsen tu empresa hacia el éxito.
          </p>
          
          {/* CTA Button */}
          <button className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-green-accent to-blue-accent rounded-full overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl">
            <span className="relative z-10">Descubre Cómo Podemos Ayudarte</span>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-accent to-green-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </button>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <div key={index} className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-3xl md:text-4xl font-bold text-green-accent mb-2">
                <AnimatedCounter end={stat.number} suffix={stat.suffix} />
              </div>
              <p className="text-gray-300 text-sm md:text-base">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Mission, Vision, Values Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {/* Misión */}
          <div 
            className={`group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg p-8 rounded-2xl border border-white/20 transition-all duration-500 hover:border-green-accent/50 hover:transform hover:scale-105 cursor-pointer ${activeCard === 0 ? 'ring-2 ring-green-accent' : ''}`}
            onClick={() => setActiveCard(activeCard === 0 ? null : 0)}
          >
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-gradient-to-br from-green-accent to-blue-accent rounded-full flex items-center justify-center shadow-lg">
              <FaHandsHelping className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mt-8 mb-4 text-center">Nuestra Misión</h3>
            <p className="text-gray-300 leading-relaxed text-center">
              <strong className="text-green-accent">Liderar la innovación contable y tecnológica</strong> en Perú, 
              especializándonos en soluciones ERP CIGE que transforman la gestión empresarial. 
              Garantizamos <strong className="text-blue-accent">seguridad, confidencialidad y excelencia</strong>, 
              consolidando nuestra posición como tu socio de confianza más estratégico.
            </p>
          </div>

          {/* Visión */}
          <div 
            className={`group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg p-8 rounded-2xl border border-white/20 transition-all duration-500 hover:border-blue-accent/50 hover:transform hover:scale-105 cursor-pointer ${activeCard === 1 ? 'ring-2 ring-blue-accent' : ''}`}
            onClick={() => setActiveCard(activeCard === 1 ? null : 1)}
          >
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-gradient-to-br from-blue-accent to-green-accent rounded-full flex items-center justify-center shadow-lg">
              <FaBullseye className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mt-8 mb-4 text-center">Nuestra Visión</h3>
            <p className="text-gray-300 leading-relaxed text-center">
              Ser <strong className="text-blue-accent">el asesor estratégico #1 en Perú</strong>, 
              reconocidos por crear soluciones que no solo resuelven problemas, sino que 
              <strong className="text-green-accent"> generan valor exponencial a largo plazo</strong>. 
              Contribuimos al desarrollo económico del país a través de la excelencia técnica y la innovación.
            </p>
          </div>

          {/* Valores */}
          <div 
            className={`group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg p-8 rounded-2xl border border-white/20 transition-all duration-500 hover:border-green-accent/50 hover:transform hover:scale-105 ${activeCard === 2 ? 'ring-2 ring-green-accent' : ''}`}
          >
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-gradient-to-br from-green-accent to-blue-accent rounded-full flex items-center justify-center shadow-lg">
              <FaBalanceScale className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mt-8 mb-6 text-center">Nuestros Valores</h3>
            <div className="space-y-4">
              {values.map((value, index) => (
                <ValueItem 
                  key={value.title}
                  icon={value.icon}
                  title={value.title}
                  description={value.description}
                  delay={index * 100}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Call to Action Final */}
        <div className="text-center bg-gradient-to-r from-green-accent/20 to-blue-accent/20 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            ¿Listo para Transformar tu Empresa?
          </h3>
          <p className="text-gray-300 text-lg mb-6 max-w-2xl mx-auto">
            Únete a más de 500 empresas que ya confían en nosotros para impulsar su crecimiento. 
            El momento de actuar es ahora.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-3 bg-gradient-to-r from-green-accent to-blue-accent text-white font-semibold rounded-full hover:scale-105 transition-all duration-300 hover:shadow-xl">
              Agenda una Consulta Gratuita
            </button>
            <button className="px-8 py-3 border-2 border-white/30 text-white font-semibold rounded-full hover:bg-white/10 transition-all duration-300">
              Conoce Nuestros Servicios
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;